import { Action, createReducer, on } from '@ngrx/store';

import { ExtractActions } from './extract.actions';
import { ExtractState } from './extract.state';
import { builtInTopics } from '../../features/extract/constans/default-topics';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { loadingPlaceholder } from '../../features/unified-datapoint/models/datapoint.model';
import { AppActions } from '../app/app.actions';
import { TopicDiff } from '../topic-diff.model';

export const initialState: ExtractState = {
  loading: false,
  loadingColumns: {},
  list: {},
  diffsByTopic: {},
  diffByDefault: true,
  updatingDatapoint: false,
  extractOrderIsDefault: false,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(ExtractActions.StartLoadList, (state, action): ExtractState => ({ ...state, error: undefined, list: {} })),
    on(
      ExtractActions.LoadList,
      (state, action): ExtractState => ({
        ...state,
        error: undefined,
        list: {},
        loadingColumns: { ...state.loadingColumns, ...action.loadingColumns },
      })
    ),
    on(
      ExtractActions.LoadListSuccess,
      (state, action): ExtractState => ({
        ...state,
        list: { ...state.list, ...action.list },
        loadingColumns: { ...state.loadingColumns, ...action.finishedColumns },
      })
    ),
    on(
      ExtractActions.LoadListFail,
      (state, action): ExtractState => ({
        ...state,
        error: action.error,
        loadingColumns: { ...state.loadingColumns, ...action.finishedColumns },
      })
    ),

    on(
      ExtractActions.LoadExtractOrder,
      ExtractActions.UpdateExtractOrder,
      (state): ExtractState => ({ ...state, loading: true, error: undefined })
    ),
    on(
      ExtractActions.LoadExtractOrderSuccess,
      (state, action): ExtractState => ({
        ...state,
        loading: false,
        extractOrder: action.order?.length ? action.order : builtInTopics.map((t) => t.id),
        // No saved selection means the order is still the built-in default; keep the flag true so
        // asset built-in checklist topics apply. Loading an order (e.g. from an embedded deal view)
        // must not silently mark a non-customized session as customized.
        extractOrderIsDefault: !action.order?.length,
      })
    ),
    on(
      ExtractActions.UpdateExtractOrderSuccess,
      (state, action): ExtractState => ({
        ...state,
        loading: false,
        extractOrder: action.order,
        extractOrderIsDefault: false,
      })
    ),
    on(
      ExtractActions.LoadExtractOrderFail,
      (state, action): ExtractState => ({ ...state, loading: false, error: action.error })
    ),

    on(
      AppActions.LoadInitDataSuccess,
      (state, action): ExtractState => ({
        ...state,
        extractOrder: action.data.assetSettings?.extractTopicIds || builtInTopics.map((t) => t.id),
        extractOrderIsDefault: !action.data.assetSettings?.extractTopicIds,
      })
    ),

    on(
      ExtractActions.MakeDiffSuccess,
      (state, action): ExtractState => ({
        ...state,
        diffsByTopic: { ...state.diffsByTopic, [action.topicId]: action.diff },
      })
    ),

    on(
      ExtractActions.MakeDiff,
      (state, action): ExtractState => ({
        ...state,
        diffsByTopic: {
          ...state.diffsByTopic,
          [action.topic.id]: {
            diffBaseline: {},
            diffColumns: {},
            diffList: {},
            diffLoading: true,
            diffCompleted: false,
            diffLoadingByColumns: action.topic.columns.reduce((acc, curr) => {
              acc[curr.id] = true;
              return acc;
            }, {}),
            diffMaximums: {},
          } as TopicDiff,
        },
      })
    ),

    on(
      ExtractActions.ClearDiffByTopic,
      (state, action): ExtractState => ({
        ...state,
        diffsByTopic: {
          ...state.diffsByTopic,
          [action.topicId]: {
            diffColumns: {},
            diffBaseline: {},
            diffList: {},
            diffLoading: false,
            diffCompleted: true,
            diffMaximums: {},
            diffLoadingByColumns: {},
          },
        },
      })
    ),

    on(
      ExtractActions.ClearDiff,
      (state, action): ExtractState => ({ ...state, diffsByTopic: initialState.diffsByTopic })
    ),

    on(ExtractActions.UpdateDatapointSuccess, (state, action): ExtractState => {
      const colId = action.event.columnId;
      const transactionId = action.event.transactionId;
      const prevCol = state.list[colId] ?? {};
      const prev = prevCol[transactionId] ?? {};

      const merged = {
        ...prev,
        ...action.datapoint,
        ...(action.event.value !== undefined ? { value: action.event.value } : {}),
        ...(action.event.sourceReferences !== undefined ? { sourceReferences: action.event.sourceReferences } : {}),
      };

      return {
        ...state,
        updatingDatapoint: false,
        list: {
          ...state.list,
          [colId]: { ...prevCol, [transactionId]: merged },
        },
      };
    }),

    on(ExtractActions.UpdateDatapoint, (state, action): ExtractState => ({ ...state, updatingDatapoint: true })),
    on(
      ExtractActions.UpdateDatapointFail,

      (state, action): ExtractState => ({ ...state, updatingDatapoint: false })
    ),
    on(
      ExtractActions.UpdateDatapointSuccess,
      (state, action): ExtractState => ({
        ...state,
        selectedDatapointForEdit:
          state.selectedDatapointForEdit?.datapointField?.fieldHash === action.event.datapointHash
            ? {
                ...state.selectedDatapointForEdit,
                datapointValue: action.event.value ?? state.selectedDatapointForEdit.datapointValue,
                sourceReferences: action.event.sourceReferences ?? state.selectedDatapointForEdit.sourceReferences,
              }
            : state.selectedDatapointForEdit,
      })
    ),
    on(
      ExtractActions.UpdateSelectedDatapointForEdit,
      (state, action): ExtractState => ({
        ...state,
        selectedDatapointForEdit: action,
      })
    ),
    on(
      ExtractActions.ReRunSingleDatapointInitiated,
      (state, action): ExtractState => ({
        ...state,
        list: {
          ...state.list,
          [action.columnId]: {
            ...state.list[action.columnId],
            [action.transactionId]: {
              value: loadingPlaceholder,
            },
          },
        },
      })
    )
  )
);

export function reducer(state: ExtractState | undefined, action: Action) {
  return featureReducer(state, action);
}
